import { Land, RainCardInformation, RodeoCardInformation } from 'src/@types'
import RainsCard from 'src/views/rain/rains-card'
import RodeoCard from 'src/views/rodeos/rodeo-card'
import WeatherCard from 'src/views/weatherCard'
import LoadCard from '../load-card'
import { useTour } from '@reactour/tour'
import { useEffect } from 'react'
import { fetchOneUser } from 'src/store/slices/userSlice'
import { AppDispatch, RootState } from 'src/store'
import { useDispatch, useSelector } from 'react-redux'
import useFirebaseAuth from 'src/@core/hooks/useFirebaseAuth'
import CardDp5 from '../card-dp5/card-dp5'
import { fetchFuncionCampo } from 'src/store/slices/reportSlice'
import { Grid } from '@mui/material'
import GrassGrowth from '../grass-growth'
import {
  fetchAuditingData,
  fetchDataCarga,
  fetchDetailedLoadData,
  fetchPastureGrowthData
} from 'src/store/slices/dashboard-slice'

import EmissionsCard from '../emission-card'
import ProductionCard from '../card-production'
import { getCampaignStartingOnJuly } from 'src/@core/utils/get-campaign'
import { fetchEmissionsDataCargaFina, fetchCarbonfootprintData } from 'src/store/slices/dp9-da1'

import CardCarga from '../card-carga'
import CardAuditoria from '../card-auditoria'
import { postOnePastureGraph } from 'src/store/slices/grazingSlice'
import { preferentCycleForModel } from 'src/@core/utils/Cycles/get-cycles'
import CarbonfootprintCard from '../carbonfootprint-card'

interface DashboardCardPlacerProps {
  land: Land
  rodeoInfo?: RodeoCardInformation
  rainCardInfo?: RainCardInformation
}

const DashboardCardPlacer = ({ land, rainCardInfo, rodeoInfo }: DashboardCardPlacerProps) => {
  const currentDate = new Date()
  const currentDateLastYear = new Date(currentDate)
  currentDateLastYear.setFullYear(currentDate.getFullYear() - 1)
  const dispatch = useDispatch<AppDispatch>()
  const { authUser } = useFirebaseAuth(dispatch)
  const { user } = useSelector((state: RootState) => state.user)
  const { setIsOpen } = useTour()
  const { functionCampo } = useSelector((state: RootState) => state.report)
  const { pastureGrowth, detailedLoad, cardCarga, auditingData } = useSelector((state: RootState) => state.dashboard)
  const { emissionsDataCargaFina, carbonfootprintData } = useSelector((state: RootState) => state.dp9Da1)
  const cycle = getCampaignStartingOnJuly(currentDate)
  const past_cycle = getCampaignStartingOnJuly(currentDateLastYear)

  const updatePastures = currentDate.getUTCDay() % 3 === 0
  const productiveCycle = preferentCycleForModel()
  const tif = functionCampo?.find(
    elemento => elemento.slugRaster === `${land.id}/${productiveCycle}/PRODUCTIVE_CAPACITY_VISUAL_RASTER.tif`
  )

  useEffect(() => {
    if (land && land.id) {
      if (updatePastures) {
        dispatch(postOnePastureGraph({ landId: land.id, cycle: cycle }))
      }
      dispatch(fetchFuncionCampo({ id: land.id, cycle: productiveCycle }))
      dispatch(fetchPastureGrowthData(land.id))
      dispatch(fetchDetailedLoadData({ landId: land.id, cycle: cycle }))
      dispatch(fetchEmissionsDataCargaFina({ landId: land.id, cycle: cycle }))
      dispatch(fetchCarbonfootprintData({ landId: land.id, cycle: past_cycle }))
      dispatch(fetchDataCarga(land.id))
      dispatch(fetchAuditingData(land.id))
    }
  }, [land])

  useEffect(() => {
    if (authUser && authUser.uid) {
      dispatch(fetchOneUser(authUser!.uid!))
    }
  }, [authUser])

  useEffect(() => {
    if (user && user.onBoarding !== true) setIsOpen(true)
  }, [user])

  return (
    <>
      <Grid container spacing={5} sx={{ mt: 2, width: '100%', alignContent: 'flex-start' }}>
        {land?.province && (
          <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
            <WeatherCard city={land.province} />
          </Grid>
        )}
        <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
          {land && <LoadCard land={land} />}
        </Grid>
        <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
          <RainsCard information={rainCardInfo} landId={land.id!} status={land.state!} />
        </Grid>
        {land?.id && tif && (
          <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
            <CardDp5 landId={land.id} percentage={tif.avgValue} />
          </Grid>
        )}
        {land?.id && pastureGrowth && pastureGrowth.length > 0 && (
          <Grid item xl={6} lg={6} md={8} sm={12} xs={12}>
            <GrassGrowth pastureGrowth={pastureGrowth} landId={land.id} />
          </Grid>
        )}
        {rodeoInfo &&
          rodeoInfo.totalHeads > 0 &&
          rodeoInfo.differentCategories.length > 0 && (
            <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
              <RodeoCard information={rodeoInfo} landId={land.id!} status={land.state!} />
            </Grid>
        )}
        <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
          {cardCarga && <CardCarga landId={land.id!} data={cardCarga} />}
        </Grid>
        {emissionsDataCargaFina?.kgCO2EqPV &&
          emissionsDataCargaFina.totalCO2eq &&
          emissionsDataCargaFina.totalCO2EqPerMeanEv &&
          emissionsDataCargaFina.kgCO2Ha &&
          land?.id && (
            <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
              <EmissionsCard
                cycle={emissionsDataCargaFina.cycle}
                kgCO2EqPV={emissionsDataCargaFina.kgCO2EqPV}
                totalCO2eq={emissionsDataCargaFina.totalCO2eq}
                totalCO2EqPerHead={emissionsDataCargaFina.totalCO2EqPerMeanEv}
                kgCO2Ha={emissionsDataCargaFina.kgCO2Ha}
                landId={land.id}
              />
            </Grid>
          )}
        {carbonfootprintData && land?.id && (
          <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
            <CarbonfootprintCard
              carbonFootprintEv={carbonfootprintData.carbonFootprintEv}
              carbonFootprintHa={carbonfootprintData.carbonFootprintHa}
              emission={carbonfootprintData.emission}
              carbonSequestration={carbonfootprintData.carbonSequestration}
              cycle={carbonfootprintData.cycle}
              landId={land.id}
            />
          </Grid>
        )}
        {detailedLoad &&
          land?.id &&
          land?.state &&
          !(
            detailedLoad.grADPV === 0 &&
            detailedLoad.productionPerArea === 0 &&
            detailedLoad.productionPerHead === 0 &&
            detailedLoad.totalProduction === 0
          ) && (
            <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
              <ProductionCard detailedLoad={detailedLoad} landId={land.id} status={land.state} />
            </Grid>
          )}
        {auditingData?.data && (
          <Grid item xl={6} lg={6} md={8} sm={12} xs={12}>
            <CardAuditoria auditingData={auditingData} />
          </Grid>
        )}
      </Grid>
    </>
  )
}
export default DashboardCardPlacer
